import { useEffect } from "react";
import { addOrcaEvent, isFullVideoData } from "../../utils";
import { OrcaWindowEvents } from "../../../globals";
import { sendVideoSplunkEvent } from "../../utils/splunk/sendVideoSplunkEvent";
import {
  SPLUNK_EVENT_NAMES,
  SPLUNK_EVENT_TYPES
} from "../../splunk-events/globals";
import { useVideoElementRefContext } from "../../contexts/VideoElementRefContext";
import { getVideoData } from "./utils";
import { VideoApiActionTypes } from "./reducer";

/**
 * A React hook that uses the Video API to get data about a video.
 * It handles three scenarios:
 * 1. Orca is provided a uuid prop but no video prop. In this case, we fetch the
 * video data, and store it in state.
 * 2. The video prop changes, and we update the video data in state to match the new value.
 * This is the behavior when a user clicks on a new video in videos-nextjs.
 * 3. The user provides no uuid prop, and the videoData is not complete. In this case we enter
 * an error state.
 *
 * @param {boolean} debug - Whether or not to enable debug mode.
 * @param {function} dispatchVideoApiEvent - A function to dispatch events related to the Video API.
 * @param {string} domain - The domain of the video.
 * @param {object} originalVideoData - The original video data. Stored in state for playthrough purposes.
 * @param {string} uuid - The UUID of the video.
 * @param {string} videoApiUrl - The URL of the Video API.
 * @param {object} currentVideoData - The video data currently saved in state. This is used by the rest of orca.
 * @param {object} videoElement - The video element.
 * @param {object} propVideoData - The video data passed in as a prop.
 */
export const useVideoApi = (deps, options) => {
  const {
    dispatchVideoApiEvent,
    domain,
    originalVideoData,
    uuid,
    videoApiUrl,
    currentVideoData,
    propVideoData,
    config,
    orcaRef
  } = deps;

  // Check options for custom handler
  const { onSetOriginalVideo = defaultSetOriginalVideo } = options;

  const videoElementRef = useVideoElementRefContext();
  const { _id: propVideoId } = propVideoData;
  const { _id: originalVideoId } = originalVideoData || {};
  const { _id: currentVideoId } = currentVideoData || {};
  const { debug, hasNextVideo, overrideReducedMotion } = config;

  const isValidVideoData = propVideoData => {
    return propVideoData && isFullVideoData(propVideoData);
  };

  const startNewVideo = () => {
    orcaRef.current.resetVideoState(hasNextVideo);
    orcaRef.current.initializeNewVideo(hasNextVideo, overrideReducedMotion);
  };

  // handle the error from the Video API.
  const handleVideoApiError = ({ message }) => {
    addOrcaEvent(OrcaWindowEvents.VIDEO_API_ERROR, message, uuid, debug);
    dispatchVideoApiEvent({
      type: VideoApiActionTypes.SET_API_FAILED
    });
    sendVideoSplunkEvent(
      videoElementRef,
      SPLUNK_EVENT_TYPES.ERROR,
      SPLUNK_EVENT_NAMES.API_ERROR,
      { errorMessage: message, uuid },
      config
    );
  };

  function defaultSetOriginalVideo({ type, videoData }) {
    dispatchVideoApiEvent({ type, videoData });
  }

  // Handle the video data returned from the Video API.
  const handleVideoData = videoDataFromApi => {
    if (!videoDataFromApi) throw Error("Video data from API returned empty.");
    addOrcaEvent(OrcaWindowEvents.VIDEO_API_DATA, "API Success", uuid, debug);

    if (!originalVideoId) {
      // Call the provided/default handler to set the original video
      onSetOriginalVideo({
        type: VideoApiActionTypes.SET_ORIGINAL_FROM_API,
        videoData: videoDataFromApi[0],
        dispatchVideoApiEvent,
        videoApiUrl,
        domain,
        orcaRef
      });
    } else {
      dispatchVideoApiEvent({
        type: VideoApiActionTypes.SET_NEXT_FROM_API,
        videoData: videoDataFromApi[0]
      });
      startNewVideo();
    }
  };

  /**
   * First video passed to the orca player is stored in state as the
   * original video and used to fetch recommended playthrough.
   */
  useEffect(() => {
    if (propVideoId && !originalVideoId) {
      onSetOriginalVideo({
        type: VideoApiActionTypes.SET_ORIGINAL,
        videoData: propVideoData,
        dispatchVideoApiEvent,
        videoApiUrl,
        domain,
        orcaRef
      });
    }

    /**
     * If there is a UUID and the video prop is missing or incomplete,
     * make a request to the Video API to get the full video data.
     */
    if (uuid && !isValidVideoData(propVideoData) && uuid !== currentVideoId) {
      getVideoData({ videoApiUrl, domain }, uuid)
        .then(handleVideoData)
        .catch(handleVideoApiError);
    } else if (
      propVideoId &&
      currentVideoId &&
      propVideoId !== currentVideoId
    ) {
      /**
       * If the video prop changes, and is no longer equal to the current video
       * data, dispatch an event to set the next video.
       */
      dispatchVideoApiEvent({
        type: VideoApiActionTypes.SET_NEXT,
        videoData: propVideoData
      });
      /**
       * If there is a current video, this is a playthrough vid,
       * so we kick off the autoplay flow
       */
      startNewVideo();
    } else if (!uuid && !isValidVideoData(propVideoData)) {
      /**
       * If there is no UUID and the video prop is not a full video data object,
       * dispatch an event to set the video API data failed.
       */
      dispatchVideoApiEvent({
        type: VideoApiActionTypes.SET_API_FAILED
      });
      sendVideoSplunkEvent(
        videoElementRef,
        SPLUNK_EVENT_TYPES.ERROR,
        SPLUNK_EVENT_NAMES.API_ERROR,
        { errorMessage: "Video missing UUID and video data is empty." },
        config
      );
    }
  }, [uuid, propVideoId, hasNextVideo]);
};
